import React, { useEffect, useState, useCallback, useMemo } from "react";
import PyramidChart from "../PyramidChart/PyramidChart";
import { DimensionamentoService } from "../../services/DimensionamentoServices";
import SummaryRegisteredData from "../BeneficiaryDataLoader/SummaryRegisteredData/SummaryRegisteredData";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import DemographicTable from "../DemographicTable/DemographicTable";
import { LuArrowUpDown } from "react-icons/lu";

const DemographicData = () => {
  const [initialLoad, setInitialLoad] = useState(true);
  const [result, setResult] = useState(null);
  const [citys, setCitys] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [interval, setInterval] = useState("3");
  const [aggregatedData, setAggregatedData] = useState(null);
  const [pyramidData, setPyramidData] = useState(null);
  const [inverted, setInverted] = useState(true);
  const [cityHq, setCityHq] = useState(null);
  const [selectedStateFilter, setSelectedStateFilter] = useState(
    "Todos os estados"
  );

  const variables = useMemo(() => ({
    total: "Todas as regiões",
    valuesMacro: "Macro-Regiões",
    valuesMicro: "Micro-Regiões",
    valuesHealthRegion: "Regiões de Saúde",
    valuesState: "Estados",
  }), []);

  const selectVariables = useMemo(() => ({
    valuesMacro: "a macro-região",
    valuesMicro: "a micro-região",
    valuesHealthRegion: "a região de saúde",
    valuesState: " estado",
  }), []);

  const [selectedVariable, setSelectedVariable] = useState("total");
  const [header, setHeader] = useState([]);

  const changeCity = useCallback(
    (event) => {
      if (!event.target.value || event.target.value === `Escolha um${selectVariables[selectedVariable]}`) {
        setSelectedCity(null);
        setCityHq(null);
        return;
      }

      const selected = result[selectedVariable].find(
        (item) => item[0] === event.target.value
      );
      setSelectedCity(selected || null);
      setCityHq(
        selectedVariable !== "total" && selectedVariable !== "valuesState"
          ? selected?.[2]
          : null
      );
    },
    [result, selectedVariable, selectVariables]
  );

  const changeVariable = useCallback(
    (event, total = false) => {
      if (total || event.target.value === "total") {
        if (result) {
          setSelectedVariable("total");
          let tempResult = JSON.parse(JSON.stringify(result));
          let totalArray = ["Total"];
          tempResult["valuesMacro"].forEach((item) => {
            item.shift();
            item.forEach((value, col) => {
              if (totalArray[col + 1] === undefined) totalArray[col + 1] = 0;
              totalArray[col + 1] += parseFloat(value);
            });
          });
          setSelectedCity(totalArray);
          setCityHq(null);
        }
      } else {
        const newVariable = event.target.value;
        setSelectedVariable(newVariable);

        if (result && result[newVariable]) {
          const newCitys = result[newVariable].map((item) => item[0]);
          setCitys(newCitys);
          setSelectedCity(null);
          setCityHq(
            event.target.value !== "total" && event.target.value !== "valuesState"
              ? null
              : null
          );
        }
      }
    },
    [result]
  );

  const filterCitysByState = useCallback(
    (stateValue) => {
      setSelectedStateFilter(stateValue);

      if (!result || !result[selectedVariable]) return;

      if (!stateValue || stateValue === "Todos os estados") {
        const allCitys = result[selectedVariable].map((item) => item[0]);
        setCitys(allCitys);
        return;
      }

      const filtered = result[selectedVariable].filter(
        (item) => item[1] === stateValue
      );

      setCitys(filtered.map((item) => item[0]));

      if (
        selectedCity &&
        filtered.find((item) => item[0] === selectedCity[0])
      ) {
      } else {
        setSelectedCity(null);
        setCityHq(null);
      }
    },
    [result, selectedVariable, selectedCity]
  );

  useEffect(() => {
    if (
      !initialLoad &&
      selectedVariable !== "total" &&
      selectedVariable !== "valuesState"
    ) {
      filterCitysByState(selectedStateFilter);
    }
  }, [selectedVariable, result, selectedStateFilter, filterCitysByState, initialLoad]);

  const handleCalculate = useCallback((r) => {
    if (initialLoad) {
      setInitialLoad(false);
      setResult(r);
      setCitys(r["valuesMacro"].map((item) => item[0]));

      const header = r["header"].map((item) => item.slice(0, -1).trim());
      const uniqueHeader = header.filter((item, index) => header.indexOf(item) === index);
      setHeader(uniqueHeader);

      let totalArray = ["Total"];
      r["valuesMacro"].forEach((item) => {
        item.slice(1).forEach((value, col) => {
          if (totalArray[col + 1] === undefined) totalArray[col + 1] = 0;
          totalArray[col + 1] += parseFloat(value);
        });
      });
      setSelectedCity(totalArray);
      setSelectedVariable("total");
    }
  }, [initialLoad]);

  const handleInvertData = () => {
    if (!pyramidData || !aggregatedData) return;

    const newPyramid = {
      ...pyramidData,
      labels: [...pyramidData.labels].reverse(),
      masculinoPerc: [...pyramidData.masculinoPerc].reverse(),
      femininoPerc: [...pyramidData.femininoPerc].reverse(),
    };

    const newAggregated = {
      ...aggregatedData,
      labels: [...aggregatedData.labels].reverse().filter((label) => label !== ""),
      masculino: [...aggregatedData.masculino].reverse(),
      feminino: [...aggregatedData.feminino].reverse(),
      total: [...aggregatedData.total].reverse(),
    };

    setPyramidData(newPyramid);
    setAggregatedData(newAggregated);
    setInverted((prev) => !prev);
  };

  function aggregateDemographicData(header, values, interval, inverted) {
    if (!values)
      return {
        masculino: [],
        feminino: [],
        total: [],
        labels: [],
        masculinoPerc: [],
        femininoPerc: [],
      };

    const intervalParsed = parseInt(interval) > 0 ? parseInt(interval) : 3;
    const masculino = [];
    const feminino = [];

    values.slice(4).forEach((value, index) => {
      const count = parseFloat(value);
      if ((index + 1) % 2 !== 0) masculino.push(count);
      else feminino.push(count);
    });

    const aggregate = (arr) => {
      const grouped = [];
      for (let i = 0; i < arr.length; i += intervalParsed) {
        grouped.push(
          arr.slice(i, i + intervalParsed).reduce((acc, val) => acc + (val || 0), 0)
        );
      }
      return grouped;
    };

    const masculinoAgg = aggregate(masculino);
    const femininoAgg = aggregate(feminino);
    const totalAgg = masculinoAgg.map((val, idx) => val + femininoAgg[idx]);

    const totalMasculino = masculino.reduce((a, b) => a + b, 0);
    const totalFeminino = feminino.reduce((a, b) => a + b, 0);

    const masculinoPerc = masculinoAgg.map((v) => (v / totalMasculino) * -100 || 0);
    const femininoPerc = femininoAgg.map((v) => (v / totalFeminino) * 100 || 0);

    const faixas = [];
    for (let i = 0; i < header.length; i += intervalParsed) {
      const inicio = header[i] || "";
      const fim = header[i + intervalParsed - 1] || "";
      faixas.push(`${inicio}-${fim}`);
    }

    return {
      masculino: inverted ? masculinoAgg.reverse() : masculinoAgg,
      feminino: inverted ? femininoAgg.reverse() : femininoAgg,
      total: inverted ? totalAgg.reverse() : totalAgg,
      labels: inverted ? faixas.reverse() : faixas,
      masculinoPerc: inverted ? masculinoPerc.reverse() : masculinoPerc,
      femininoPerc: inverted ? femininoPerc.reverse() : femininoPerc,
    };
  }

  useEffect(() => {
    const fetchData = async () => {
      const resp = await DimensionamentoService.getBeneficiariosDemografico();
      if (resp && resp["valuesMacro"]) {
        handleCalculate(resp);
      }
    };
    if (initialLoad) {
      fetchData();
    }
  }, [initialLoad, handleCalculate]);

  useEffect(() => {
    if (selectedCity && header.length > 0) {
      const data = aggregateDemographicData(header, selectedCity, interval, inverted);
      setAggregatedData(data);
      setPyramidData(data);
    } else {
      setAggregatedData(null);
      setPyramidData(null);
    }
  }, [selectedCity, interval, header, inverted]);

  return (
    <div className="min-h-screen py-8">
      <div className="w-full max-w-7xl mx-auto px-4 lg:px-8">
        {initialLoad && (
          <div className="flex justify-center items-center min-h-[300px]">
            <svg
              className="animate-spin h-10 w-10 text-emerald-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span className="ml-4 text-emerald-600 text-lg italic">
            Carregando dados demográficos...
          </span>
          </div>
        )}

        {!initialLoad && result && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-emerald-800 mb-2">
                Dados Demográficos da Operadora
              </h1>
              <p className="text-emerald-600 text-lg">
                Análise populacional por região e faixa etária
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6 border border-emerald-200">
              <div className="flex items-center mb-4">
                <div className="w-1 h-8 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-semibold text-emerald-800">
                  Filtros de Análise
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <FormControl fullWidth>
                    <InputLabel
                      id="mainFilter"
                      sx={{
                        color: "#059669",
                        "&.Mui-focused": { color: "#059669" },
                      }}
                    >
                      Filtrar dados por região
                    </InputLabel>
                    <Select
                      variant="outlined"
                      labelId="mainFilter"
                      label="Filtrar dados por região"
                      value={selectedVariable || "total"}
                      onChange={(event) => changeVariable(event)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#a7f3d0" },
                          "&:hover fieldset": { borderColor: "#059669" },
                          "&.Mui-focused fieldset": { borderColor: "#059669" },
                        },
                        "& .MuiSelect-select": { color: "#065f46" },
                      }}
                    >
                      {Object.keys(variables).map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {variables[item]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {selectedVariable !== "total" && selectedVariable !== "valuesState" && (
                  <div className="space-y-2">
                    <FormControl fullWidth>
                      <InputLabel
                        id="stateAuxFilter"
                        sx={{
                          color: "#059669",
                          "&.Mui-focused": { color: "#059669" },
                        }}
                      >
                        Filtrar regiões por estado
                      </InputLabel>
                      <Select
                        variant="outlined"
                        labelId="stateAuxFilter"
                        label="Filtrar regiões por estado"
                        value={selectedStateFilter || "Todos os estados"}
                        onChange={(event) =>
                          filterCitysByState(event.target.value)
                      }
                        renderValue={(selected) => {
                          if (!selected || selected === "")
                            return "Todos os estados";
                          return selected;
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#a7f3d0" },
                            "&:hover fieldset": { borderColor: "#059669" },
                            "&.Mui-focused fieldset": { borderColor: "#059669" },
                          },
                          "& .MuiSelect-select": { color: "#065f46" },
                        }}
                      >
                        <MenuItem value="Todos os estados">
                          <em>Todos os estados</em>
                        </MenuItem>
                        {(result?.valuesState || []).map((item, index) => (
                          <MenuItem key={index} value={item[0]}>
                            {item[0]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                )}

                {selectedVariable !== "total" && (
                  <div className="space-y-2">
                    <FormControl fullWidth>
                      <InputLabel
                        id="cityFilter"
                        sx={{
                          color: "#059669",
                          "&.Mui-focused": { color: "#059669" },
                        }}
                      >
                        Escolha um{selectVariables[selectedVariable]}
                      </InputLabel>
                      <Select
                        variant="outlined"
                        labelId="cityFilter"
                        label={`Escolha um${selectVariables[selectedVariable]}`}
                        value={
                          selectedCity
                            ? selectedCity[0]
                            : `Escolha um${selectVariables[selectedVariable]}`
                        }
                        onChange={(event) => changeCity(event)}
                        renderValue={(selected) => {
                          if (!selected || selected === "")
                            return `Escolha um${selectVariables[selectedVariable]}`;
                          return selected;
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#a7f3d0" },
                            "&:hover fieldset": { borderColor: "#059669" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#059669",
                            },
                          },
                          "& .MuiSelect-select": { color: "#065f46" },
                        }}
                      >
                        <MenuItem
                          value={`Escolha um${selectVariables[selectedVariable]}`}
                        >
                          <em>Escolha um{selectVariables[selectedVariable]}</em>
                        </MenuItem>
                        {citys.map((item, index) => (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                )}

                <div className="space-y-2">
                  <TextField
                    label="Intervalo da faixa etária"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: "#059669",
                        "&.Mui-focused": { color: "#059669" },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#a7f3d0" },
                        "&:hover fieldset": { borderColor: "#059669" },
                        "&.Mui-focused fieldset": { borderColor: "#059669" },
                      },
                      "& .MuiOutlinedInput-input": { color: "#065f46" },
                    }}
                  />
                </div>
              </div>
              {cityHq && (
                <p className="text-center pt-6 text-emerald-800">
                  Município Sede:{" "}
                  <span className="text-emerald-600">{cityHq}</span>
                </p>
              )}
            </div>

            <div
              className={`grid ${
                pyramidData && aggregatedData ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
              } gap-6`}
            >
              {pyramidData && aggregatedData ? (
                <>
                  <div className="bg-white rounded-2xl shadow-xl p-6 border border-emerald-200 hover:shadow-2xl transition-shadow duration-300">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-1 h-8 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full mr-3"></div>
                        <div>
                          <h3 className="text-xl font-semibold text-emerald-800">
                            Pirâmide Populacional
                          </h3>
                          <p className="text-emerald-600 text-sm">
                            Distribuição por gênero e idade
                          </p>
                        </div>
                      </div>
                      <LuArrowUpDown
                        onClick={() => handleInvertData()}
                        className="cursor-pointer text-emerald-800 text-2xl hover:opacity-75 transition-opacity duration-150 ease-in-out"
                        title="Inverter ordem das faixas etárias"
                      />
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 border border-emerald-100">
                      <PyramidChart
                        header={pyramidData.labels}
                        masculinoPerc={pyramidData.masculinoPerc}
                        femininoPerc={pyramidData.femininoPerc}
                      />
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-xl p-6 border border-emerald-200 hover:shadow-2xl transition-shadow duration-300">
                    <div className="flex items-center mb-6">
                      <div className="w-1 h-8 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full mr-3"></div>
                      <div>
                        <h3 className="text-xl font-semibold text-emerald-800">
                          Tabela Demográfica
                        </h3>
                        <p className="text-emerald-600 text-sm">Dados numéricos detalhados</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 border border-emerald-100">
                      <DemographicTable header={aggregatedData.labels} values={aggregatedData} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full bg-white rounded-2xl shadow-xl p-6 border border-emerald-200 flex items-center justify-center text-emerald-600 text-lg italic">
                  Escolha um{selectVariables[selectedVariable]} para visualizar os dados demográficos
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-emerald-200 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6">
                <div className="flex items-center">
                  <div className="w-1 h-8 bg-white rounded-full mr-3"></div>
                  <h3 className="text-xl font-semibold text-white">
                    Resumo dos Dados Registrados
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <SummaryRegisteredData
                  beneficiaryData={result}
                  selectedVariable={selectedVariable}
                  selectedCity={selectedCity}
                  tableLock={false}
                  search=""
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemographicData;
